/**
 * Image Utilities & Resilient Fallback Engine for Dynamic Web Platform
 * Prevents broken images when remote hosting or external links fail.
 * Supports all website types: Tour & Travel (e.g. Bellva Tour), Adventure, Gathering,
 * Corporate, Legal, Tech, E-Commerce, Culinary, Medical, Photography, and General.
 */

// Curated high-resolution professional theme images (reliable CDN with WebP support)
export const PROFESSIONAL_THEME_FALLBACKS: Record<string, string[]> = {
  travel_adventure: [
    'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1200&q=80',
  ],
  teknologi: [
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1200&q=80',
  ],
  bisnis_corporate: [
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80',
  ],
  legal_law: [
    'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1453728013993-6d66e9c9123a?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1505664194779-8beaceb93744?auto=format&fit=crop&w=1200&q=80',
  ],
  products_ecommerce: [
    'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1572635196237-14b3f281503f?auto=format&fit=crop&w=1200&q=80',
  ],
  culinary_food: [
    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80',
  ],
  default: [
    'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80',
  ],
};

/**
 * Return a deterministic high-resolution fallback image based on article id/title and category
 */
export function getResilientFallbackImage(titleOrId: string = '', categoryName: string = ''): string {
  const cat = (categoryName || '').toLowerCase();
  const text = (titleOrId + ' ' + cat).toLowerCase();
  let pool = PROFESSIONAL_THEME_FALLBACKS.default;

  if (
    text.includes('adventure') ||
    text.includes('wisata') ||
    text.includes('outbound') ||
    text.includes('gathering') ||
    text.includes('tour') ||
    text.includes('travel') ||
    text.includes('trip') ||
    text.includes('rafting') ||
    text.includes('offroad') ||
    text.includes('camping') ||
    text.includes('lembang') ||
    text.includes('bandung')
  ) {
    pool = PROFESSIONAL_THEME_FALLBACKS.travel_adventure;
  } else if (
    text.includes('hukum') ||
    text.includes('law') ||
    text.includes('legal') ||
    text.includes('advokat') ||
    text.includes('pengacara') ||
    text.includes('notaris') ||
    text.includes('peraturan')
  ) {
    pool = PROFESSIONAL_THEME_FALLBACKS.legal_law;
  } else if (
    text.includes('teknologi') ||
    text.includes('desain') ||
    text.includes('digital') ||
    text.includes('web') ||
    text.includes('software') ||
    text.includes('ai')
  ) {
    pool = PROFESSIONAL_THEME_FALLBACKS.teknologi;
  } else if (
    text.includes('kuliner') ||
    text.includes('makanan') ||
    text.includes('food') ||
    text.includes('resto') ||
    text.includes('cafe') ||
    text.includes('kopi')
  ) {
    pool = PROFESSIONAL_THEME_FALLBACKS.culinary_food;
  } else if (
    text.includes('produk') ||
    text.includes('umkm') ||
    text.includes('belanja') ||
    text.includes('shop') ||
    text.includes('souvenir')
  ) {
    pool = PROFESSIONAL_THEME_FALLBACKS.products_ecommerce;
  } else if (
    text.includes('bisnis') ||
    text.includes('perusahaan') ||
    text.includes('corporate') ||
    text.includes('manajemen') ||
    text.includes('konsultan')
  ) {
    pool = PROFESSIONAL_THEME_FALLBACKS.bisnis_corporate;
  }

  // Hash titleOrId to select deterministic image index
  let hash = 0;
  for (let i = 0; i < titleOrId.length; i++) {
    hash = (hash << 5) - hash + titleOrId.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % pool.length;
  return pool[index];
}

/**
 * Handle image onerror event to replace broken remote URL with high-res curated image
 */
export function handleImageError(
  event: React.SyntheticEvent<HTMLImageElement, Event>,
  titleOrId?: string,
  categoryName?: string
) {
  const img = event.currentTarget;
  if (!img) return;

  const fallback = getResilientFallbackImage(titleOrId, categoryName);
  if (img.src !== fallback) {
    img.src = fallback;
  }
}
